import { isPlatformBrowser } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { LoginPopupComponent } from '../../components/login-popup/login-popup.component';
import { WelcomePopupComponent } from '../../components/welcome-popup/welcome-popup.component';
import { CryptoDepositComponent } from '../../components/crypto-deposit/crypto-deposit.component';
import { betModels } from '../../models/betModels';
import { BetService } from '../../services/bet.service';
import { LocalStoreService } from '../../services/local-store.service';
import { ReunionStreamingService } from '../../services/reunion-streaming.service';
import { SupabaseService } from '../../services/supabase.service';
import { SettingsService } from '../../services/settings.service';
import { WelcomeModalService } from '../../services/welcome-modal.service';
import { VirtualsVerdictRefreshService } from '../../services/virtuals-verdict-refresh.service';
import { isProviderLobbyUrl } from '../../guards/providers-enabled.guard';
import { setGeneralSettingsData } from 'src/app/store/general-settings/general-settings.actions';
import { Store } from '@ngrx/store';
import { selectUserDetailsData } from 'src/app/store/user-details/user-details.selectors';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  providers: [DialogService],
})
export class MainLayoutComponent
  implements OnInit, OnDestroy, AfterContentChecked
{
  userBalance = 0;
  isHome = false;
  /**
   * Full-bleed "lobby mode": the provider lobby (GoldenRace) is a vendor
   * application with its OWN navigation, bet slip and history, so the site's
   * left column (countries), right column (bet slip + widgets) and the
   * streaming bar are not context around it — they are competing chrome that
   * squeezes the game into a third of the screen. In lobby mode the middle
   * column takes the whole width and those blocks are not rendered at all.
   */
  isProviderLobby = false;
  /**
   * Gates the centre column's min-width override (see the scss): with
   * streaming off, every page lays out exactly as it did without the feature.
   */
  readonly streamingEnabled = this.reunionStreaming.enabled;
  isBrowser = false;
  rightMenu;
  leftMenu;
  sidebarAppendTarget: string | null = null;
  username = '';
  bettingSlipStatus = false;
  ref: DynamicDialogRef;
  betSlip$: BehaviorSubject<any> = new BehaviorSubject(null);
  info: any;
  raceDate: any;
  betStakes = betModels.stakes;
  clearAllSelectedRunners$ = new Subject();
  private _activeBetSlipIndex = 0;
  maintanceHide = false;
  get activeBetSlipIndex(): number {
    return this._activeBetSlipIndex;
  }
  set activeBetSlipIndex(value: number) {
    this._activeBetSlipIndex = value;
  }
  private _unsubscribeAll = new Subject();
  allBetsOnCart$ = this.betService.allAddedBetsInCart$.asObservable();
  private latestUserDetails: any = null;
  private welcomeShownThisSession = false;
  // The dialogs this layout opens itself (the post-registration welcome and
  // the deposit picker it can lead to). PrimeNG 11's DialogService attaches
  // them to the APPLICATION, not to this component, and never closes them on
  // its own — a modal left open when the layout is destroyed (navigating to
  // /auth or /maintenance) would float over the next page with the body
  // scroll still locked (`p-overflow-hidden`). Kept so ngOnDestroy can close
  // them; cleared as soon as they close by themselves.
  private welcomeRef: DynamicDialogRef | null = null;
  private depositRef: DynamicDialogRef | null = null;
  private cryptoRef: DynamicDialogRef | null = null;
  private destroyed = false;
  /**
   * The getDefaultCountry() request started for the current "no account"
   * stretch (publishCountry). Its handle is kept so a real account landing
   * first can cancel it: its answer would otherwise overwrite that account's
   * currency with the site default.
   */
  private defaultCountryRequest: any = null;

  constructor(
    public dialogService: DialogService,
    public authService: AuthService,
    private settingService: SettingsService,
    public router: Router,
    private store: Store,
    public route: ActivatedRoute,
    public betService: BetService,
    private cdref: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any,
    private localStoreService: LocalStoreService,
    private supabase: SupabaseService,
    private welcomeService: WelcomeModalService,
    private verdictRefresh: VirtualsVerdictRefreshService,
    private reunionStreaming: ReunionStreamingService
  ) {
    this.getLocalDate.bind(this);
    // takeUntil: the layout is destroyed and recreated around /auth and
    // /maintenance, and a router subscription left behind by each instance
    // would keep flipping flags on a component nobody renders.
    this.router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe((event) => {
      // NavigationStart carries the TARGET url and is the ONLY event that does
      // before the route component is created — `router.url` still reports the
      // page we are leaving all the way through RoutesRecognized, the guards
      // and the resolvers. Deriving lobby mode from it means the side columns,
      // the streaming bar and the container gutters are gone BEFORE the lobby
      // page is instantiated, so the page's first height measurement (its
      // ngAfterViewInit) already sees its final position instead of one taken
      // while the home chrome is still above it — which is what left the frame
      // short until the first scroll fired a viewport resize.
      //
      // Only the chrome-REMOVING direction is applied optimistically, for
      // both flags — applyPendingRouteFlags says why.
      //
      // Only these four events touch the flags: re-deriving on every event
      // (RoutesRecognized, GuardsCheck*, Resolve*, Activation*) would read the
      // stale `router.url` and undo the flip one event later.
      if (event instanceof NavigationStart) {
        this.applyPendingRouteFlags(event.url);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        // Cancel / Error: the navigation did NOT happen, so `router.url` is
        // still the page we are on — this undoes the optimistic flip above.
        this.applyRouteFlags(this.router.url);
      }
      if (event instanceof NavigationEnd) {
        if (this.route.snapshot?.queryParamMap?.get('fbclid'))
          this.router.navigateByUrl('/');
      }
    });
  }
  /**
   * True for the provider lobby's own urls. Matched on the PATH only (query
   * and fragment stripped) and on whole segments, so '/virtualsomething' is a
   * different page, not a lobby.
   */
  isProviderLobbyUrl(url: string): boolean {
    return isProviderLobbyUrl(url);
  }

  private applyRouteFlags(url: string): void {
    this.isHome = (url || '') === '/';
    this.isProviderLobby = this.isProviderLobbyUrl(url);
  }

  /**
   * NavigationStart: the target url, while the page we are LEAVING is still in
   * the outlet. Only changes that REMOVE chrome may be applied here; anything
   * that adds chrome waits for NavigationEnd, which activates the destination
   * in the same change-detection pass, and a cancelled or failed navigation
   * restores both flags from `router.url`.
   *
   * `isProviderLobby` false -> true removes blocks (streaming bar, both
   * columns, gutters), and that is the whole point: the lobby page must be
   * created into its final position. So entering the lobby flips it early.
   *
   * `isProviderLobby` true -> false is the opposite: it RE-MOUNTS the
   * countries column, the streaming player, the bet slip and the toolbars
   * around the lobby page that is still in the outlet — with the live game in
   * it — for as long as the destination chunk takes. That is the "game still
   * on screen inside the normal site" symptom. Leaving the lobby therefore
   * waits for NavigationEnd (the lobby page hides its own frame meanwhile).
   *
   * `isHome` follows the same rule. Turning it ON early would mount home-only
   * chrome on top of the page the player is still looking at for the whole
   * navigation — app-home-announcement, the quarté block (mobile-visible) and
   * the countries column at md/lg — and home is a lazy chunk, so on a cold
   * cache that is hundreds of milliseconds of home widgets grafted above a
   * race or account page. Only true -> false is applied optimistically.
   */
  private applyPendingRouteFlags(url: string): void {
    if (this.isProviderLobbyUrl(url)) {
      this.isProviderLobby = true;
    }
    if ((url || '') !== '/') {
      this.isHome = false;
    }
  }

  /**
   * Publishes the account's country and currency to the header and the bet
   * slips — or, with no account in the store, the site default.
   *
   * "No account" is decided on the CURRENCY, not on `country` being there:
   * the cleared store (initialUserDetailsState — boot, a logout here or in
   * another tab) carries a truthy `country` object whose `currency` is '',
   * and publishing that blanked the guest UI's currency and stakes until a
   * reload.
   *
   * The default is asked for once per "no account" stretch, not on every
   * store write in it, and a real account landing while that request is out
   * cancels it — the boot sequence (cleared store first, the account a moment
   * later) would otherwise let a late default overwrite the account's own
   * currency.
   */
  private publishCountry(user: any): void {
    if (user && user.country && user.country.currency) {
      this.cancelDefaultCountry();
      this.authService.currentCountry$.next(user.country);
      this.authService.currentCurrency$.next(user.country.currency);
      return;
    }
    if (this.defaultCountryRequest) {
      return;
    }
    // getDefaultCountry() subscribes itself and hands back the subscription
    // (typed as an Observable); `true` stands in when it returns nothing, so
    // the stretch still asks only once.
    this.defaultCountryRequest = this.authService.getDefaultCountry() || true;
  }

  private cancelDefaultCountry(): void {
    const request = this.defaultCountryRequest;
    this.defaultCountryRequest = null;
    if (request && typeof request.unsubscribe === 'function') {
      request.unsubscribe();
    }
  }

  maintanceSet() {
    this.localStoreService.setItem('maintanceHide', true);
    this.maintanceHide = true;
  }
  ngOnInit(): void {
    // Synchronously, from the url we already have: the constructor's
    // subscription only fires on the NEXT router event, so without this the
    // layout's FIRST client render would draw the columns and then drop them
    // one change-detection pass later.
    //
    // It does NOT remove the flash on a hard load / refresh of /virtuals, and
    // nothing in this component can. Both guards on that route bail on the
    // server — ProvidersEnabledGuard returns a UrlTree to '/' when the platform
    // is not a browser (providers-enabled.guard.ts:51) and AuthGuard reads the
    // user out of localStorage, which is null under SSR — so the server renders
    // HOME for the url /virtuals, columns and streaming bar included, and the
    // lobby only appears once the client has bootstrapped and re-run the
    // guards. Fixing that means letting the SSR pass render the lobby shell,
    // which is a change to guards shared with every account route; it is
    // deliberately out of scope here.
    this.applyRouteFlags(this.router.url);
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.sidebarAppendTarget = this.isBrowser ? 'body' : null;

    if (this.localStoreService.getItem('user')) {
      this.store
        .select(selectUserDetailsData)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((user) => this.publishCountry(user));
    } else {
      this.authService.getDefaultCountry();
    }

    if (this.localStoreService.getItem('maintanceHide') !== true) {
      this.localStoreService.setItem('maintanceHide', false);
    }
    this.maintanceHide = this.localStoreService.getItem('maintanceHide');
    if (this.localStoreService.getItem('user')) {
      this.getUserAccount();
    }
    this.getSetting();

    // Keeps the per-account virtuals verdict current in an open tab (login
    // change, tab back in view after the TTL). Browser-only and idempotent —
    // the service checks both — so a recreated layout does not double it.
    this.verdictRefresh.start();

    // One-time post-registration welcome. Two triggers, same evaluation:
    //   - user details resolving in the store (post-reload boot, or the
    //     refresh that follows an in-place auto-login),
    //   - an auth popup closing (login/register release a suppression hold —
    //     the welcome must never cover the one-click credentials screen).
    this.store
      .select(selectUserDetailsData)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user) => {
        this.latestUserDetails = user;
        this.maybeShowWelcome();
      });
    this.welcomeService.nudge$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.maybeShowWelcome());

    this.router.events
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter(
          (event) =>
            event instanceof NavigationStart && !event.url.startsWith('/race/')
        ),
        tap(
          (event) => (
            (this.bettingSlipStatus = false),
            (this.betService.totalAllBetsOnCart = 0),
            this.betService.allAddedBetsInCart$.next([])
          )
        )
      )
      .subscribe();

    this.betSlip$
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter((info) => Boolean(info)),
        tap((info) => {
          this.bettingSlipStatus = info.bettingSlipStatus;

          let currentBetRanks: any = {
            [info.selectedBet?.type === 'ita'
              ? 2
              : info.selectedBet?.type === 'trita'
              ? 3
              : 1]: [],
          };

          if (
            [
              'exacta',
              'trifecta',
              'pmu_tierce',
              'pmu_quarte_plus',
              'pmu_quinte_plus',

              'trio',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { ...currentBetRanks, [2]: [] };
          if (['trita'].includes(info.selectedBet?.type))
            currentBetRanks = { ...currentBetRanks, [3]: [] };
          if (
            [
              'trifecta',
              'pmu_tierce',
              'pmu_quarte_plus',
              'pmu_quinte_plus',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { ...currentBetRanks, [3]: [] };
          if (
            ['pmu_quarte_plus', 'pmu_quinte_plus'].includes(
              info.selectedBet?.type
            )
          )
            currentBetRanks = { ...currentBetRanks, [4]: [] };

          if (
            [
              'quinella',
              'trifecta',
              'exacta',
              'pmu_tierce',
              'pmu_tierce_standard',
              'pmu_quarte_plus',
              'pmu_quinte_plus',
              'pmu_quinte_plus_standard',
              'pmu_quarte_plus_standard',
              'trio',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { ...currentBetRanks, [5]: [] };

          if (
            [
              'pmu_tierce_standard',
              'pmu_quarte_plus_standard',
              'pmu_quinte_plus_standard',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { [5]: [] };
          if (['pmu_quinte_plus'].includes(info.selectedBet?.type))
            currentBetRanks = { ...currentBetRanks, [6]: [] };
          this.info = this.bettingSlipStatus
            ? {
                ...info,
                selectedRunnersObjIterable: [],
                selectedRunnersObj: Boolean(info.selectedRunners?.length)
                  ? info.selectedRunners.reduce((prev, curr) => {
                      let runnerOrder =
                        curr.runnerOrder === 0 ? 1 : curr.runnerOrder;
                      if (
                        info.selectedBet?.type === 'quinella' &&
                        runnerOrder > 1
                      )
                        runnerOrder = 5;
                      if (info.selectedBet?.type === 'ita') runnerOrder = 2;
                      if (info.selectedBet?.type === 'trita') runnerOrder = 3;
                      if (
                        [
                          'pmu_quarte_plus_standard',
                          'pmu_tierce_standard',
                          'pmu_quinte_plus_standard',
                        ].includes(info.selectedBet?.type)
                      ) {
                        runnerOrder = 5;
                      }

                      if (!prev[runnerOrder]) {
                        return { ...prev, [runnerOrder]: [curr] };
                      } else {
                        return {
                          ...prev,
                          [runnerOrder]: [...prev[runnerOrder], curr],
                        };
                      }
                    }, currentBetRanks)
                  : currentBetRanks,
              }
            : null;
          if (Boolean(this.info?.selectedRunnersObj))
            this.info = {
              ...this.info,
              selectedRunnersObjIterable: Object.keys(
                this.info.selectedRunnersObj
              ).map((m) => [parseInt(m, 10), this.info.selectedRunnersObj[m]]),
            };
          if (Boolean(this.info?.details))
            this.raceDate = this.getLocalDate(this.info.details);
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    if (this.ref) {
      this.ref.close();
    }
    // After _unsubscribeAll: the onClose handlers below are gone by now, so
    // closing the welcome cannot chain into opening the deposit picker.
    [this.welcomeRef, this.depositRef, this.cryptoRef].forEach((ref) => {
      if (ref) {
        ref.close();
      }
    });
    this.welcomeRef = null;
    this.depositRef = null;
    this.cryptoRef = null;
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  showConfirmBetPopup(): void {
    this.authService
      .getCurrentUser()
      .pipe(take(1))
      .subscribe((currentUser) => {
        if (!currentUser) {
          this.ref = this.dialogService.open(LoginPopupComponent, {
            showHeader: true,
            dismissableMask: true,
            data: {
              reload: false,
            },
            closable: true,
            styleClass: 'login-modal',
            contentStyle: {
              'border-radius': '4px',
              padding: '0',
              'z-index': '999',
            },
          });
          this.ref.onClose.subscribe((loginSuccess?) => {
            if (loginSuccess) {
              this.betService.showConfirmBetPopupEvent$.next();
            }
          });
        } else {
          this.betService.showConfirmBetPopupEvent$.next();
        }
      });
  }

  // Through the shared single-flight request, which also dispatches the
  // payload into the store: at boot the header asks for the very same
  // account, and the verdict refresh's login trigger waits on this request
  // instead of firing its own.
  //
  // The figures are read from the STORE once the answer has landed, not from
  // the payload handed back: every caller receives that payload even when the
  // service refused to store it (an answer for an account that has since
  // logged out or been replaced), and the store holds only the account signed
  // in now. Read once per answer, as before — a standing subscription would
  // also move userBalance on every Supabase setBalances write, and the right
  // sidenav compares that input against its own balance feed to raise the
  // "money deposited" toast.
  getUserAccount(): void {
    this.verdictRefresh
      .refreshAccount()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.store
          .select(selectUserDetailsData)
          .pipe(take(1))
          .subscribe((stored) => {
            this.userBalance = stored ? stored.balance : 0;
            this.username = stored ? stored.username : '';
          });
      });
  }

  // ---- one-time post-registration welcome --------------------------------

  // Consumes the pending flag exactly once, as soon as the freshly-registered
  // user's role is known and no auth popup is open. Consumed-but-ineligible
  // (non-TND / non-clientRegistered) clears silently — the flag can never
  // leak into a later session or another account.
  private maybeShowWelcome(): void {
    if (!this.isBrowser || this.welcomeShownThisSession) {
      return;
    }
    if (!this.welcomeService.hasPending() || this.welcomeService.suppressed) {
      return;
    }
    const user = this.latestUserDetails;
    if (!user || !user.role) {
      return;
    }

    this.welcomeService.clearPending();
    const credentials = this.welcomeService.consumeCredentials();

    const currency =
      user.countryCurrency || (user.country && user.country.currency) || '';
    if (user.role !== 'clientRegistered') {
      return;
    }
    // The TND-only rule applies to the plain welcome (normal registration).
    // CREDENTIALS mode (one-click) must show for every currency — it is the
    // only place the generated password is ever displayed.
    if (!credentials && currency !== 'TND') {
      return;
    }

    this.welcomeShownThisSession = true;
    const welcomeRef = this.dialogService.open(WelcomePopupComponent, {
      showHeader: false,
      dismissableMask: true,
      data: { credentials },
      width:
        typeof window !== 'undefined' && window.innerWidth > 768
          ? '420px'
          : '92vw',
      contentStyle: {
        padding: '0',
        'border-radius': '12px',
        overflow: 'visible',
        background: 'transparent',
      },
      baseZIndex: 10000,
      styleClass: 'welcome-modal',
    });
    this.welcomeRef = welcomeRef;
    welcomeRef.onClose
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        if (this.welcomeRef === welcomeRef) {
          this.welcomeRef = null;
        }
        if (result === 'deposit') {
          this.openTndDepositModal();
        }
      });
  }

  // "Deposit now" from the welcome modal. The TND payment module is lazy
  // (bundled with the account area), so the modal component is imported on
  // demand instead of dragging the whole module into the eager bundle.
  private openTndDepositModal(): void {
    import(
      'src/app/modules/tnd-payment/components/tnd-deposit-modal/tnd-deposit-modal.component'
    ).then(({ TndDepositModalComponent }) => {
      // The chunk can land after the layout is gone; a modal opened now
      // would have nobody left to close it.
      if (this.destroyed) {
        return;
      }
      const depositRef = this.dialogService.open(TndDepositModalComponent, {
        showHeader: false,
        dismissableMask: true,
        width:
          typeof window !== 'undefined' && window.innerWidth > 768
            ? '560px'
            : '95vw',
        contentStyle: { 'max-height': '92vh', overflow: 'auto', padding: '0' },
        baseZIndex: 10000,
        styleClass: 'tnd-payment-modal',
      });
      this.depositRef = depositRef;
      // Same crypto fall-through contract as the wallet page picker.
      depositRef.onClose
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((result) => {
          if (this.depositRef === depositRef) {
            this.depositRef = null;
          }
          if (result && result.openCrypto) {
            const cryptoRef = this.dialogService.open(CryptoDepositComponent, {
              data: { status: 'status' },
              showHeader: true,
              dismissableMask: true,
              width: '50vw',
              contentStyle: { 'min-height': '65vh', padding: '0' },
              baseZIndex: 10000,
            });
            this.cryptoRef = cryptoRef;
            cryptoRef.onClose
              .pipe(takeUntil(this._unsubscribeAll))
              .subscribe(() => {
                if (this.cryptoRef === cryptoRef) {
                  this.cryptoRef = null;
                }
              });
          }
        });
    });
  }

  getLocalDate(race) {
    const startTimeUtc =
      race.StartTimeUtc !== null && race.StartTimeUtc !== undefined
        ? race.StartTimeUtc.replace(/-/g, '/')
        : undefined;
    const scheduleTimeUtc =
      race.ScheduleTimeUtc !== null && race.ScheduleTimeUtc !== undefined
        ? race.ScheduleTimeUtc.replace(/-/g, '/')
        : undefined;
    const localDate =
      startTimeUtc !== undefined
        ? new Date(startTimeUtc + ' UTC')
        : scheduleTimeUtc !== undefined
        ? new Date(scheduleTimeUtc + ' UTC')
        : null;
    return localDate !== null ? localDate : new Date();
  }

  getSetting() {
    this.settingService.getGeneralSettings().subscribe((data) => {
      this.store.dispatch(setGeneralSettingsData({ data }));
    });
  }

  clearAll(): void {
    this.clearAllSelectedRunners$.next();
  }

  logOut() {
    this.authService.logOut();
  }
}
